/**
 * Centralized token management utility
 * Handles JWT token storage, validation, and expiration
 */

const TOKEN_KEY = 'mailroom_jwt_token';
const TOKEN_EXPIRY_KEY = 'mailroom_jwt_expiry';
const USER_KEY = 'mailroom_user_data';
const REFRESH_BUFFER_MS = 5 * 60 * 1000; // 5 minutes buffer

class TokenManager {
  /**
   * Store JWT token with expiration tracking
   * @param {string} token - JWT token
   * @param {number} expiresIn - Token expiration in seconds
   * @param {Object} userData - User data to store
   */
  static setToken(token, expiresIn = 3600, userData = null) {
    try {
      const expiryTime = Date.now() + (expiresIn * 1000);
      
      localStorage.setItem(TOKEN_KEY, token);
      localStorage.setItem(TOKEN_EXPIRY_KEY, expiryTime.toString());
      
      if (userData) {
        localStorage.setItem(USER_KEY, JSON.stringify(userData));
      }
      
      console.log(`[TokenManager] Token stored, expires at: ${new Date(expiryTime).toISOString()}`);
    } catch (error) {
      console.error('[TokenManager] Error storing token:', error);
    }
  }

  /**
   * Get stored JWT token
   * @returns {string|null} JWT token or null if not found/expired
   */
  static getToken() {
    try {
      const token = localStorage.getItem(TOKEN_KEY);
      const expiry = localStorage.getItem(TOKEN_EXPIRY_KEY);
      
      if (!token || !expiry) {
        return null;
      }
      
      const expiryTime = parseInt(expiry, 10);
      const now = Date.now();
      
      // Check if token is expired
      if (now >= expiryTime) {
        console.log('[TokenManager] Token expired, clearing storage');
        this.clearToken();
        return null;
      }
      
      return token;
    } catch (error) {
      console.error('[TokenManager] Error getting token:', error);
      return null;
    }
  }

  /**
   * Check if token needs refresh (within buffer time)
   * @returns {boolean} True if token needs refresh
   */
  static needsRefresh() {
    try {
      const expiry = localStorage.getItem(TOKEN_EXPIRY_KEY);
      
      if (!expiry) {
        return true;
      }
      
      const expiryTime = parseInt(expiry, 10);
      const now = Date.now();
      
      // Check if we're within the refresh buffer
      return (expiryTime - now) <= REFRESH_BUFFER_MS;
    } catch (error) {
      console.error('[TokenManager] Error checking refresh need:', error);
      return true;
    }
  }

  /**
   * Get token expiration time
   * @returns {Date|null} Expiration date or null
   */
  static getTokenExpiry() {
    try {
      const expiry = localStorage.getItem(TOKEN_EXPIRY_KEY);
      return expiry ? new Date(parseInt(expiry, 10)) : null;
    } catch (error) {
      console.error('[TokenManager] Error getting token expiry:', error);
      return null;
    }
  }

  /**
   * Get stored user data
   * @returns {Object|null} User data or null
   */
  static getUserData() {
    try {
      const userData = localStorage.getItem(USER_KEY);
      return userData ? JSON.parse(userData) : null;
    } catch (error) {
      console.error('[TokenManager] Error getting user data:', error);
      return null;
    }
  }

  /**
   * Clear all stored authentication data
   */
  static clearToken() {
    try {
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(TOKEN_EXPIRY_KEY);
      localStorage.removeItem(USER_KEY);
      console.log('[TokenManager] All tokens cleared');
    } catch (error) {
      console.error('[TokenManager] Error clearing tokens:', error);
    }
  }

  /**
   * Check if user is authenticated
   * @returns {boolean} True if authenticated with valid token
   */
  static isAuthenticated() {
    return this.getToken() !== null;
  }

  /**
   * Get time until token expires in milliseconds
   * @returns {number} Milliseconds until expiration, 0 if expired
   */
  static getTimeUntilExpiry() {
    try {
      const expiry = localStorage.getItem(TOKEN_EXPIRY_KEY);
      
      if (!expiry) {
        return 0;
      }
      
      const expiryTime = parseInt(expiry, 10);
      const now = Date.now();
      
      return Math.max(0, expiryTime - now);
    } catch (error) {
      console.error('[TokenManager] Error getting time until expiry:', error);
      return 0;
    }
  }

  /**
   * Parse JWT token to extract claims (without verification)
   * @param {string} token - JWT token
   * @returns {Object|null} Token claims or null
   */
  static parseTokenClaims(token) {
    try {
      if (!token) return null;
      
      const parts = token.split('.');
      if (parts.length !== 3) return null;
      
      const payload = parts[1];
      const decoded = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
      
      return JSON.parse(decoded);
    } catch (error) {
      console.error('[TokenManager] Error parsing token claims:', error);
      return null;
    }
  }

  /**
   * Validate token structure and basic claims
   * @param {string} token - JWT token
   * @returns {boolean} True if token structure is valid
   */
  static validateTokenStructure(token) {
    try {
      if (!token || typeof token !== 'string') {
        return false;
      }
      
      const parts = token.split('.');
      if (parts.length !== 3) {
        return false;
      }
      
      const claims = this.parseTokenClaims(token);
      if (!claims) {
        return false;
      }
      
      // Check for essential JWT claims (exp is required, others are flexible)
      if (!claims.exp) {
        console.warn('[TokenManager] Token missing expiration claim (exp)');
        return false;
      }
      
      // Check if token has some form of user identification
      // Common JWT claims: sub (subject), userId, id, username, email
      const userIdClaims = ['sub', 'userId', 'id', 'username', 'email'];
      const hasUserIdentification = userIdClaims.some(claim => claims.hasOwnProperty(claim) && claims[claim]);
      
      if (!hasUserIdentification) {
        console.warn('[TokenManager] Token missing user identification claims');
        return false;
      }
      
      // Validate expiration is a valid timestamp
      const expiration = claims.exp;
      if (typeof expiration !== 'number' || expiration <= 0) {
        console.warn('[TokenManager] Token has invalid expiration value');
        return false;
      }
      
      return true;
    } catch (error) {
      console.error('[TokenManager] Error validating token structure:', error);
      return false;
    }
  }

  /**
   * Extract user information from token claims
   * @param {string} token - JWT token
   * @returns {Object|null} User information or null
   */
  static extractUserInfo(token) {
    try {
      const claims = this.parseTokenClaims(token);
      if (!claims) return null;

      // Extract user information with fallbacks
      const userInfo = {
        id: claims.sub || claims.userId || claims.id || null,
        username: claims.username || claims.preferred_username || claims.email || null,
        email: claims.email || null,
        name: claims.name || claims.given_name || claims.family_name || null,
        exp: claims.exp || null,
        iat: claims.iat || null
      };

      return userInfo;
    } catch (error) {
      console.error('[TokenManager] Error extracting user info:', error);
      return null;
    }
  }
}

export default TokenManager;
